import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { groupDigits, pad2, shapeKey } from '@/lib/format'
import { PointCloud } from '@/components/generals/point-cloud'
import { HoverThumbnail } from '@/components/projects/hover-thumbnail'

interface ProjectsTableProps {
    projects: Array<ProjectSummary>
}

type Filter = 'all' | 'timber' | 'steel' | 'concrete' | 'masonry'
type TypeFilter = 'all' | 'civic' | 'cultural' | 'housing' | 'infrastructure'
type Sort = 'year' | 'area'
type View = 'list' | 'grid'

const FILTERS: Array<{ id: Filter, label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'timber', label: 'Timber' },
    { id: 'steel', label: 'Steel' },
    { id: 'concrete', label: 'Concrete' },
    { id: 'masonry', label: 'Masonry' }
]

const TYPES: Array<{ id: TypeFilter, label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'civic', label: 'Civic' },
    { id: 'cultural', label: 'Cultural' },
    { id: 'housing', label: 'Housing' },
    { id: 'infrastructure', label: 'Infrastructure' }
]

const VIEWS: Array<{ id: View, label: string }> = [
    { id: 'list', label: 'List' },
    { id: 'grid', label: 'Grid' }
]

const SORTS: Array<{ id: Sort, label: string }> = [
    { id: 'year', label: 'Year' },
    { id: 'area', label: 'Area' }
]

const GRID = 'desk:grid desk:grid-cols-[4rem_minmax(0,2.2fr)_minmax(0,1fr)_4.5rem_7rem_minmax(0,1.2fr)_8.5rem] desk:items-center desk:gap-6'

const isInProgress = (status: string): boolean => status.trim().toLowerCase() === 'in progress'

/**
 * The project index: filter chips by structure and by type, a sort control, a list or grid view,
 * rows that glide to their new places, a trailing point-cloud thumbnail on hover and an inline
 * preview on touch screens.
 */
export const ProjectsTable: FC<ProjectsTableProps> = ({ projects }): ReactNode => {
    const [filter, setFilter] = useState<Filter>('all')
    const [type, setType] = useState<TypeFilter>('all')
    const [view, setView] = useState<View>('list')
    const [sort, setSort] = useState<Sort>('year')
    const [hovered, setHovered] = useState<ProjectSummary | null>(null)
    const [expanded, setExpanded] = useState<string | null>(null)
    const rowRefs = useRef(new Map<string, HTMLLIElement>())
    const positions = useRef(new Map<string, number>())

    const numbers = useMemo(() => new Map(projects.map((project, index) => [project.id, pad2(index + 1)])), [projects])

    const rows = useMemo(() => {
        const visible = projects
            .filter(project => filter === 'all' || project.structuralSystem.toLowerCase().includes(filter))
            .filter(project => type === 'all' || project.buildingType.trim().toLowerCase() === type)

        return [...visible].sort((a, b) => (sort === 'year' ? b.year - a.year : b.areaM2 - a.areaM2) || a.name.localeCompare(b.name))
    }, [projects, filter, type, sort])

    useLayoutEffect(() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const next = new Map<string, number>()

        rowRefs.current.forEach((element, id) => {
            const top = element.offsetTop
            const previous = positions.current.get(id)

            next.set(id, top)

            if (reduced || typeof element.animate !== 'function') return

            if (previous === undefined) {
                if (positions.current.size > 0) element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 420, easing: 'ease-out' })

                return
            }

            const delta = previous - top

            if (Math.abs(delta) < 1) return

            element.animate(
                [{ transform: `translate3d(0, ${delta}px, 0)` }, { transform: 'translate3d(0, 0, 0)' }],
                { duration: 650, easing: 'cubic-bezier(0.2, 0.7, 0.1, 1)' }
            )
        })

        positions.current = next
    }, [rows])

    return (
        <div>
            <div className='flex flex-col gap-5 border-b border-hairline px-gutter pb-6 xl:flex-row xl:items-end xl:justify-between'>
                <div className='flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:gap-x-10'>
                <div>
                    <p className='hud mb-3 text-mute' id='filter-label'>
                        Structure
                    </p>

                    <div role='group' aria-labelledby='filter-label' className='flex flex-wrap gap-2'>
                        {FILTERS.map(item => (
                            <button
                                key={item.id}
                                type='button'
                                aria-pressed={filter === item.id}
                                onClick={() => setFilter(item.id)}
                                className={cn(
                                    'hud h-9 border px-3 transition-colors duration-300',
                                    filter === item.id ? 'border-ink bg-ink text-background' : 'border-hairline text-mute hover:border-mute hover:text-ink'
                                )}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <p className='hud mb-3 text-mute' id='type-label'>
                        Type
                    </p>

                    <div role='group' aria-labelledby='type-label' className='flex flex-wrap gap-2'>
                        {TYPES.map(item => (
                            <button
                                key={item.id}
                                type='button'
                                aria-pressed={type === item.id}
                                onClick={() => setType(item.id)}
                                className={cn(
                                    'hud h-9 border px-3 transition-colors duration-300',
                                    type === item.id ? 'border-ink bg-ink text-background' : 'border-hairline text-mute hover:border-mute hover:text-ink'
                                )}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
                </div>

                <div className='flex flex-wrap items-end gap-6'>
                    <div>
                        <p className='hud mb-3 text-mute' id='view-label'>
                            View
                        </p>

                        <div role='group' aria-labelledby='view-label' className='flex border border-hairline'>
                            {VIEWS.map(item => (
                                <button
                                    key={item.id}
                                    type='button'
                                    aria-pressed={view === item.id}
                                    onClick={() => {
                                        setView(item.id)
                                        setHovered(null)
                                    }}
                                    className={cn(
                                        'hud h-9 px-3 transition-colors duration-300',
                                        view === item.id ? 'bg-ink text-background' : 'text-mute hover:text-ink'
                                    )}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className='hud mb-3 text-mute' id='sort-label'>
                            Sort by
                        </p>

                        <div role='group' aria-labelledby='sort-label' className='flex border border-hairline'>
                            {SORTS.map(item => (
                                <button
                                    key={item.id}
                                    type='button'
                                    aria-pressed={sort === item.id}
                                    onClick={() => setSort(item.id)}
                                    className={cn(
                                        'hud h-9 px-3 transition-colors duration-300',
                                        sort === item.id ? 'bg-ink text-background' : 'text-mute hover:text-ink'
                                    )}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <p className='hud pb-2.5 text-mute' aria-live='polite'>
                        Showing {pad2(rows.length)} / {pad2(projects.length)}
                    </p>
                </div>
            </div>

            {view === 'list' ? (
                <>
                    <div className={cn('hud hidden border-b border-hairline px-gutter py-4 text-mute', GRID)} aria-hidden='true'>
                        <span>
                            No.
                        </span>

                        <span>
                            Project
                        </span>

                        <span>
                            City
                        </span>

                        <span className='text-right'>
                            Year
                        </span>

                        <span className='text-right'>
                            Area
                        </span>

                        <span>
                            System
                        </span>

                        <span>
                            Status
                        </span>
                    </div>

                    <ul className='relative' onPointerLeave={() => setHovered(null)}>
                        {rows.map(project => {
                            const progress = isInProgress(project.status)
                            const isOpen = expanded === project.id

                            return (
                                <li
                                    key={project.id}
                                    ref={element => {
                                        if (element) rowRefs.current.set(project.id, element)
                                        else rowRefs.current.delete(project.id)
                                    }}
                                    onPointerEnter={event => {
                                        if (event.pointerType === 'mouse') setHovered(project)
                                    }}
                                    className='group relative border-b border-hairline bg-background transition-colors duration-300 desk:hover:bg-panel'
                                >
                                    <span
                                        aria-hidden='true'
                                        className={cn(
                                            'absolute inset-y-0 left-0 z-[1] w-[3px] origin-top bg-signal transition-transform duration-500 ease-instrument',
                                            hovered?.id === project.id || isOpen ? 'scale-y-100' : 'scale-y-0'
                                        )}
                                    />

                                    <div className={cn('relative grid grid-cols-2 gap-x-6 gap-y-4 py-6 pl-gutter pr-16 desk:py-6 desk:pr-gutter', GRID)}>
                                        <div className='col-span-2 flex items-center justify-between desk:col-span-1 desk:block'>
                                            <span className='hud text-mute'>
                                                <span className='sr-only'>
                                                    Number
                                                </span>
                                                {numbers.get(project.id)}
                                            </span>
                                        </div>

                                        <div className='col-span-2 min-w-0 desk:col-span-1'>
                                            <Link
                                                href={`/projects/${project.slug}`}
                                                onFocus={() => setHovered(project)}
                                                onBlur={() => setHovered(null)}
                                                className='inline-flex max-w-full items-center gap-3 font-heading text-[26px] font-medium leading-tight tracking-[-0.02em] text-ink after:absolute after:inset-0 after:content-[""] desk:text-[26px]'
                                            >
                                                <span className='truncate transition-transform duration-500 ease-instrument desk:group-hover:translate-x-1' data-mdlfy-cms={`projects:${project.id}:name`}>
                                                    {project.name}
                                                </span>

                                                <ArrowUpRight
                                                    className='hidden size-4 shrink-0 text-mute opacity-0 transition-[opacity,transform] duration-500 ease-instrument group-hover:translate-x-0.5 group-hover:opacity-100 desk:block'
                                                    strokeWidth={1.5}
                                                    aria-hidden='true'
                                                />
                                            </Link>
                                        </div>

                                        <Cell label='City'>
                                            <span data-mdlfy-cms={`projects:${project.id}:city`}>
                                                {project.city}
                                            </span>
                                        </Cell>

                                        <Cell label='Year' align='right'>
                                            {project.year}
                                        </Cell>

                                        <Cell label='Area' align='right'>
                                            <span className='whitespace-nowrap'>
                                                <span>
                                                    {groupDigits(project.areaM2)}
                                                </span>

                                                <span className='text-mute'>
                                                    &nbsp;m²
                                                </span>
                                            </span>
                                        </Cell>

                                        <Cell label='System'>
                                            <span data-mdlfy-cms={`projects:${project.id}:structural_system`}>
                                                {project.structuralSystem}
                                            </span>
                                        </Cell>

                                        <Cell label='Status'>
                                            <span className='inline-flex items-center gap-2.5 whitespace-nowrap'>
                                                <span aria-hidden='true' className={cn('inline-block size-1.5', progress ? 'bg-signal' : 'bg-point')} />

                                                <span className={progress ? 'text-signal' : 'text-ink'} data-mdlfy-cms={`projects:${project.id}:status`}>
                                                    {project.status}
                                                </span>
                                            </span>
                                        </Cell>
                                    </div>

                                    <button
                                        type='button'
                                        aria-expanded={isOpen}
                                        aria-controls={`project-preview-${project.id}`}
                                        onClick={() => setExpanded(isOpen ? null : project.id)}
                                        className='absolute right-0 top-0 z-[2] flex size-14 items-center justify-center border-b border-l border-hairline bg-background text-ink desk:hidden'
                                    >
                                        <Plus className={cn('size-4 transition-transform duration-300', isOpen && 'rotate-45')} strokeWidth={1.5} aria-hidden='true' />

                                        <span className='sr-only'>
                                            {isOpen ? 'Hide preview of ' : 'Show preview of '}
                                            {project.name}
                                        </span>
                                    </button>

                                    <div id={`project-preview-${project.id}`} hidden={!isOpen} className='relative z-[2] border-t border-hairline desk:hidden'>
                                        {isOpen && (
                                            <PointCloud
                                                shape={shapeKey(project.shape, project.slug)}
                                                label={`Point cloud of ${project.name}`}
                                                budget='thumb'
                                                rotate='always'
                                                rotateSpeed={8}
                                                effects
                                                frame
                                                interactive
                                                className='h-64 w-full'
                                            />
                                        )}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </>
            ) : (
                <ul className='grid gap-px border-b border-hairline bg-hairline sm:grid-cols-2 desk:grid-cols-3'>
                    {rows.map(project => (
                        <li key={project.id} className='group relative bg-background'>
                            <Link href={`/projects/${project.slug}`} className='block'>
                                <PointCloud
                                    shape={shapeKey(project.shape, project.slug)}
                                    label={`Point cloud of ${project.name} in ${project.city}`}
                                    budget='thumb'
                                    rotate='hover'
                                    rotateSpeed={14}
                                    effects
                                    frame
                                    className='aspect-[4/3] w-full border-b border-hairline'
                                >
                                    <span className='pointer-events-none absolute inset-y-0 left-0 z-[2] w-[3px] origin-top scale-y-0 bg-signal transition-transform duration-500 ease-instrument group-hover:scale-y-100' aria-hidden='true' />
                                </PointCloud>

                                <span className='flex items-end justify-between gap-6 px-5 py-5'>
                                    <span className='min-w-0'>
                                        <span className='hud block text-mute'>
                                            <span className='sr-only'>
                                                Number
                                            </span>
                                            {numbers.get(project.id)}
                                        </span>

                                        <span className='mt-2 block truncate font-heading text-[clamp(22px,2.2vw,30px)] font-medium leading-tight tracking-[-0.02em] text-ink' data-mdlfy-cms={`projects:${project.id}:name`}>
                                            {project.name}
                                        </span>

                                        <span className='hud mt-2 flex gap-3 text-mute'>
                                            <span data-mdlfy-cms={`projects:${project.id}:city`}>
                                                {project.city}
                                            </span>

                                            <span>
                                                {project.year}
                                            </span>
                                        </span>
                                    </span>

                                    <ArrowUpRight className='size-5 shrink-0 text-ink transition-transform duration-500 ease-instrument group-hover:-translate-y-1 group-hover:translate-x-1' strokeWidth={1.25} aria-hidden='true' />
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {rows.length === 0 && (
                <p className='hud px-gutter py-16 text-center text-mute'>
                    No projects use this structure yet.
                </p>
            )}

            <HoverThumbnail project={hovered} />
        </div>
    )
}

interface CellProps {
    label: string
    align?: 'left' | 'right'
    children: ReactNode
}

const Cell: FC<CellProps> = ({ label, align = 'left', children }): ReactNode => {
    return (
        <div className={cn('hud min-w-0 text-ink', align === 'right' && 'desk:text-right')}>
            <span className='block text-mute desk:sr-only'>
                {label}
            </span>

            <span className='mt-1 block truncate desk:mt-0'>
                {children}
            </span>
        </div>
    )
}
